import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";

const HASSLES = [
  {
    label: "Installer",
    className: "left-[6%] top-[16%]",
    tx: 420,
    ty: 142,
    start: 0,
    delay: "0s",
  },
  {
    label: "Equipment supplier",
    className: "left-[28%] top-[8%]",
    tx: 286,
    ty: 176,
    start: 0.08,
    delay: "-1.4s",
  },
  {
    label: "Bank",
    className: "left-[12%] top-[38%]",
    tx: 380,
    ty: 54,
    start: 0.16,
    delay: "-0.7s",
  },
  {
    label: "Government portal",
    className: "left-[2%] top-[64%]",
    tx: 440,
    ty: -78,
    start: 0.24,
    delay: "-2.1s",
  },
  {
    label: "DISCOM",
    className: "left-[32%] top-[74%]",
    tx: 238,
    ty: -142,
    start: 0.32,
    delay: "-1s",
  },
  {
    label: "Inverter app",
    className: "left-[40%] top-[42%]",
    tx: 170,
    ty: 2,
    start: 0.4,
    delay: "-2.7s",
  },
  {
    label: "Service team",
    className: "left-[18%] top-[56%]",
    tx: 332,
    ty: -58,
    start: 0.48,
    delay: "-0.3s",
  },
];

export const Problem = () => {
  const ref = useRef(null);
  const [resolved, setResolved] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);

  useEffect(() => {
    const section = ref.current;
    if (!section) return;

    let frame = 0;
    const updateProgress = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const travel = Math.max(rect.height - viewport, 1);
      const raw = -rect.top / travel;
      const progress = Math.min(Math.max(raw, 0), 1);
      const pullProgress = Math.min(Math.max((progress - 0.6) / 0.34, 0), 1);
      const arrowProgress = Math.min(Math.max((progress - 0.18) / 0.38, 0), 1);

      section.style.setProperty("--problem-progress", progress.toFixed(3));
      section.style.setProperty("--problem-pull", pullProgress.toFixed(3));
      section.style.setProperty("--problem-arrow", arrowProgress.toFixed(3));
      setPullProgress(pullProgress);
      setResolved(progress > 0.88);
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateProgress);
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    requestUpdate();

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section
      ref={ref}
      id="problem"
      className={`problem-magnet relative overflow-hidden bg-cream py-24 sm:py-32 lg:min-h-[230vh] lg:overflow-visible lg:py-0 ${resolved ? "is-resolved" : ""}`}
      style={{ "--problem-progress": 0, "--problem-pull": 0, "--problem-arrow": 0 }}
      data-testid="problem-section"
    >
      <div className="pointer-events-none absolute -left-28 top-1/4 h-72 w-72 rounded-full bg-amber/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-28 bottom-10 h-80 w-80 rounded-full bg-teal/10 blur-3xl" />

      <div className="relative z-10 mx-auto flex max-w-[1400px] flex-col justify-center px-5 sm:px-8 lg:sticky lg:top-[70px] lg:min-h-[calc(100vh-70px)] lg:py-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="eyebrow mb-6 text-amber">Why this exists</p>
          <h2 className="display-serif text-4xl leading-[1.02] tracking-[-0.035em] text-navy sm:text-5xl lg:text-[3.5rem] xl:text-[3.9rem]">
            Going solar means managing an ecosystem you never signed up for.
          </h2>
        </div>

        <div className="mt-7 grid items-center gap-6 lg:grid-cols-[1.05fr_0.24fr_0.9fr] lg:gap-6 xl:mt-9">
          <div className="relative min-h-[360px] overflow-hidden rounded-[2rem] border border-navy/10 bg-white/70 p-5 shadow-2xl shadow-navy/5 sm:min-h-[420px] lg:min-h-[430px] xl:min-h-[480px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(217,119,6,0.13),transparent_34%),radial-gradient(circle_at_70%_78%,rgba(13,148,136,0.12),transparent_32%)]" />

            {HASSLES.map((hassle, index) => (
              <HasslePill key={hassle.label} hassle={hassle} index={index} pullProgress={pullProgress} />
            ))}
          </div>

          <div className="relative flex h-20 items-center justify-center lg:hidden" aria-hidden>
            <svg className="h-full w-24 overflow-visible" viewBox="0 0 96 80" fill="none">
              <path
                className="problem-arrow-path problem-arrow-mobile"
                pathLength="1"
                d="M48 6 C48 24, 48 42, 48 62"
                stroke="#D97706"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <path
                className="problem-arrow-path problem-arrow-mobile"
                pathLength="1"
                d="M32 48 L48 64 L64 48"
                stroke="#D97706"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="relative hidden items-center justify-center lg:flex" aria-hidden>
            <svg
              className="h-[72px] w-[180px] overflow-visible drop-shadow-[0_0_18px_rgba(217,119,6,0.28)]"
              viewBox="0 0 180 72"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
            >
              <path
                className="problem-arrow-path"
                pathLength="1"
                d="M12 36 H154"
                stroke="#D97706"
                strokeWidth="5"
                strokeLinecap="round"
              />
              <path
                className="problem-arrow-path"
                pathLength="1"
                d="M134 18 L158 36 L134 54"
                stroke="#D97706"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="problem-card relative mx-auto w-full max-w-[520px] rounded-[2.2rem] bg-navy-900 p-7 text-white shadow-2xl shadow-navy/25 sm:p-9 lg:min-h-[430px] xl:min-h-[480px]">
            <div className="absolute inset-0 rounded-[2.2rem] border border-white/10" />
            <div className="relative z-10">
              <div className="mb-12 flex items-center justify-between gap-4">
                <span className="rounded-full border border-white/12 bg-white/8 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-white/58">
                  SunShell One App
                </span>
              </div>

              <h3 className="display-serif text-4xl leading-tight tracking-[-0.03em] sm:text-5xl">
                <span className="text-amber-400">One standard.</span> One accountable partner.
              </h3>

              <div className="mt-9 grid gap-3 text-white/68">
                {[
                  "Assessment and proposal",
                  "Financing guidance",
                  "Installation visibility",
                  "Monitoring and support",
                ].map((item) => (
                  <p key={item} className="flex items-center gap-3">
                    <CheckCircle2 size={18} className="shrink-0 text-teal-400" />
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HasslePill = ({ hassle, index, pullProgress }) => {
  const pull = Math.min(Math.max((pullProgress - hassle.start) * 2.25, 0), 1);
  const fade = Math.min(Math.max((pull - 0.58) * 2.4, 0), 1);
  const scale = 1 - pull * 0.36;

  return (
    <span
      className={`problem-pill absolute z-20 ${hassle.className}`}
      style={{
        opacity: 1 - fade,
        transform: `translate3d(${pull * hassle.tx}px, ${pull * hassle.ty}px, 0) scale(${scale})`,
        "--float-delay": hassle.delay,
      }}
      data-testid={`hassle-pill-${index}`}
    >
      <span className="problem-pill-float block rounded-full border border-navy/10 bg-cream px-4 py-2 text-sm font-semibold text-navy shadow-lg shadow-navy/8">
        {hassle.label}
      </span>
    </span>
  );
};
